import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import CollapsibleSection from "components/Section";
import { Grid } from "components/Grid";
import { LineChart } from "components/Charts/LineChart";
import { TDataTableColumn } from "components/Grid/types";
import useReportFilters from "../useReportFilters";
import useReportData from "../useReportData";
import useGridDetailsContextMenu from "components/Grid/hooks/useGridDetailsContextMenu";
import {
  alarmFlagLabels,
  getOverviewDashboard,
  groupAlarmsByType,
} from "../api";
import { TMeterMapPoint } from "../types";
import ReportSubHeader from "../components/ReportSubHeader";
import ReportFiltersBar from "../components/ReportFiltersBar";
import ReportBand from "../components/ReportBand";
import KpiCard from "../components/KpiCard";
import MetersMap from "../components/MetersMap";

/** Height that lines the alarm grid up with the 400px chart next to it. */
const ALARM_GRID_HEIGHT = 400;

const topConsumersColumns: TDataTableColumn[] = [
  { dataField: "identifierId", caption: "identifier", dataType: "string" },
  {
    dataField: "consumption",
    caption: "consumptionCubicMeter",
    dataType: "number",
    sortOrder: "desc",
  },
];

const errorMetersColumns: TDataTableColumn[] = [
  { dataField: "identifierId", caption: "identifier", dataType: "string" },
  { dataField: "alarmLabel", caption: "alarmType", dataType: "string" },
];

const OverviewView = () => {
  const { t } = useTranslation();
  const { filters, applyFilters, isReady } = useReportFilters({
    withTopN: true,
  });
  const {
    data,
    isLoading,
    isRefetching,
    lastUpdated,
    lastAttempt,
    refreshIntervalMs,
    refresh,
  } = useReportData(
    getOverviewDashboard,
    filters,
    isReady
  );

  const meterDetailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/meters/details/${row.id}` : undefined
  );

  const alarmsByType = useMemo(
    () => (data ? groupAlarmsByType(data.waterMeterWithErrors) : []),
    [data]
  );

  const topConsumers = useMemo(
    () =>
      (data?.topNWaterUsage || []).map((item) => ({
        id: item.waterMeterId,
        identifierId: item.identifierId || item.waterMeterId,
        consumption: item.usage,
      })),
    [data]
  );

  const metersWithErrors = useMemo(
    () =>
      (data?.waterMeterWithErrors || []).map((item) => ({
        id: item.waterMeterId,
        identifierId: item.identifierId || item.waterMeterId,
        alarmLabel: alarmFlagLabels(item.latestAlarmOnWm).join("، "),
      })),
    [data]
  );

  /**
   * Every meter the overview service gives coordinates for: the period's top
   * consumers plus the meters carrying an alarm. A meter present in both lists
   * is merged into one point that reports both roles.
   */
  const mapPoints = useMemo<TMeterMapPoint[]>(() => {
    const byId = new Map<string, TMeterMapPoint>();

    (data?.topNWaterUsage || []).forEach((item) => {
      byId.set(item.waterMeterId, {
        id: item.waterMeterId,
        identifierId: item.identifierId || item.waterMeterId,
        latitude: item.latitude,
        longitude: item.longitude,
        consumption: item.usage,
        isTopConsumer: true,
        hasAlarm: false,
      });
    });

    (data?.waterMeterWithErrors || []).forEach((item) => {
      const existing = byId.get(item.waterMeterId);
      const alarmLabel = alarmFlagLabels(item.latestAlarmOnWm).join("، ");
      if (existing) {
        existing.hasAlarm = true;
        existing.alarmLabel = alarmLabel;
        return;
      }
      byId.set(item.waterMeterId, {
        id: item.waterMeterId,
        identifierId: item.identifierId || item.waterMeterId,
        latitude: item.latitude,
        longitude: item.longitude,
        alarmLabel,
        isTopConsumer: false,
        hasAlarm: true,
      });
    });

    return Array.from(byId.values());
  }, [data]);

  /** Usage of the top-N meters, and what share of the region's total that is. */
  const topConsumersUsage = useMemo(
    () => topConsumers.reduce((sum, item) => sum + item.consumption, 0),
    [topConsumers]
  );

  const topConsumersShare = useMemo(() => {
    if (!data?.totalWaterUsage) return undefined;
    return `${((topConsumersUsage / data.totalWaterUsage) * 100).toFixed(1)}%`;
  }, [data, topConsumersUsage]);

  const averageTopConsumerUsage = useMemo(() => {
    if (!topConsumers.length) return undefined;
    return Number((topConsumersUsage / topConsumers.length).toFixed(1));
  }, [topConsumers, topConsumersUsage]);

  const topNBadge = `${(filters.topN || 0).toLocaleString("fa-IR")} ${t(
    "meter"
  )}`;

  return (
    <>
      <ReportSubHeader
        title={t("overallReport")}
        lastUpdated={lastUpdated}
        lastAttempt={lastAttempt}
        refreshIntervalMs={refreshIntervalMs}
        isLoading={isLoading}
        isRefetching={isRefetching}
        onRefresh={refresh}
      />
      <ReportFiltersBar
        filters={filters}
        onApply={applyFilters}
        isLoading={isLoading}
        showTopN
      />

      <div className="flex flex-col gap-4 p-4 h-full overflow-auto [&>*]:shrink-0">
        {/* ================ Band 1 — only the top-N consumers ================ */}
        <ReportBand
          title={t("topConsumersBand")}
          badge={topNBadge}
          description={t("topConsumersBandDesc")}
          tone="accent"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard
            title={t("topConsumersUsage")}
            value={data ? topConsumersUsage : undefined}
            unit={t("cubicMeter")}
            iconName="MdTrendingUp"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("topConsumersShare")}
            value={topConsumersShare}
            caption={t("topConsumersShareCaption")}
            hint={t("topConsumersShareHint")}
            iconName="MdOutlinePieChart"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("averageTopConsumerUsage")}
            value={averageTopConsumerUsage}
            unit={t("cubicMeter")}
            iconName="MdOutlineAnalytics"
            isLoading={isLoading}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <CollapsibleSection
            title={t("consumptionPerMeter")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "column",
                    name: t("consumption"),
                    data: topConsumers.map((item) => item.consumption),
                  },
                ]}
                xAxis={{
                  categories: topConsumers.map((item) => item.identifierId),
                }}
                yAxis={{ title: { text: t("cubicMeter") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("metersList")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="table"
            minHeight={ALARM_GRID_HEIGHT}
          >
            <Grid
              gridKey="overviewTopConsumers"
              id="overviewTopConsumersGrid"
              gridName={t("topConsumers")}
              dataSource={topConsumers}
              keyExpr="id"
              columns={topConsumersColumns}
              rowContextMenu={meterDetailsContextMenu}
              height={ALARM_GRID_HEIGHT}
              hasSearchPanel={false}
              hasPagination={false}
              hasGroupingFeatures={false}
              hasFilterBuilder={false}
              allowedChangeScrolling={true}
            />
          </CollapsibleSection>
        </div>

        {/* ====================== Band 2 — the map ====================== */}
        <ReportBand
          title={t("metersMap")}
          description={t("metersMapDesc")}
          hint={t("metersMapHint")}
          tone="neutral"
        />

        <CollapsibleSection
          title={t("metersMap")}
          collapsible={false}
          isLoading={isLoading}
          skeleton="map"
          minHeight={560}
          className="shrink-0"
        >
          <MetersMap points={mapPoints} height={560} isLoading={isLoading} />
        </CollapsibleSection>

        {/* ====== Band 3 — every meter in the filtered region (page tail) ====== */}
        <ReportBand
          title={t("allMetersBand")}
          badge={t("allMetersScope")}
          description={t("allMetersBandDesc")}
          tone="neutral"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard
            title={t("totalConsumptionInRange")}
            value={data?.totalWaterUsage}
            unit={t("cubicMeter")}
            iconName="MdWaterDrop"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("metersWithAlarm")}
            value={data?.waterMeterWithErrors.length}
            iconName="MdWarningAmber"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("alarmTypesCount")}
            value={data ? alarmsByType.length : undefined}
            iconName="MdOutlineRule"
            isLoading={isLoading}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <CollapsibleSection
            title={t("alarmsByType")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "column",
                    name: t("alarmsCount"),
                    data: alarmsByType.map((item) => item.value),
                  },
                ]}
                xAxis={{ categories: alarmsByType.map((item) => item.name) }}
                yAxis={{ title: { text: t("count") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("metersWithAlarm")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="table"
            minHeight={ALARM_GRID_HEIGHT}
          >
            <Grid
              gridKey="overviewMetersWithErrors"
              id="overviewMetersWithErrorsGrid"
              gridName={t("metersWithAlarm")}
              dataSource={metersWithErrors}
              keyExpr="id"
              columns={errorMetersColumns}
              rowContextMenu={meterDetailsContextMenu}
              height={ALARM_GRID_HEIGHT}
              hasSearchPanel={false}
              hasPagination={true}
              hasGroupingFeatures={false}
              hasFilterBuilder={false}
              allowedChangeScrolling={true}
            />
          </CollapsibleSection>
        </div>
      </div>
    </>
  );
};

export default OverviewView;
